import { api } from '../../services/apiClient';

export interface Payment {
  payment_id: number; kydemy_id: number | null; datetime: string; type: string; method: string;
  amount: string; assoc_name: string | null; months: string | null;
  invoice_number: string | null; payer: string | null; teller: string | null;
  /** Kydemy mete el formato de su BD en estos dos; los limpia `detalleCobro()` en IngresosPanel. */
  assoc_code?: string | null; comment?: string | null;
  /** Para las fotos del renglón (alumno y cajero), como en Kydemy. */
  contact_id?: number | null; teller_id?: number | null;
  /** Enlace al perfil desde la Caja (29-sep): maestro comprador y si cajero/comprador tienen ficha de profesor. */
  payer_user_id?: number | null; teller_es_profe?: number | boolean; payer_es_profe?: number | boolean;
}
/** Plan de membresía, para el chip de color por renglón (1D, B3, FEM…). */
export interface PlanChip { name: string; code: string | null; color: string | null; active?: number; deleted?: number }
export interface DayResp {
  data: Payment[];
  plans?: PlanChip[];
  totals: { byType: Record<string, number>; byMethod: Record<string, number>; grand: number; count: number };
}

// 29-jul-2026: faltaban `license`, `class`, `refund` y `voucher_voucher` → 437 pagos del histórico
// se veían con el nombre crudo en inglés en la ficha del alumno.
// Nombres de concepto tal como los usa el estudio (Carlos 15-ago): la cuota del mes es la
// MENSUALIDAD y `license` es la INSCRIPCIÓN (no una licencia ni una matrícula).
export const TYPE_LABEL: Record<string, string> = {
  membership: 'Mensualidad', event: 'Evento', other: 'Otro pago',
  voucher_trial: 'Clase de prueba', voucher: 'Bono de clases', voucher_voucher: 'Bono de clases',
  product: 'Vitrina', license: 'Inscripción', class: 'Clase suelta', refund: 'Devolución',
  // 🚨 17-sep-2026 (Carlos: «pusiste en código en pagos y adeudos late fee, quítalo»): `late_fee` es
  // un debt_type, no un tipo de pago, y faltaba aquí — la ficha del alumno hace
  // `TYPE_LABEL[d.debt_type] || d.debt_type`, así que las 139 multas se pintaban «late_fee» en crudo.
  // Cualquier debt_type nuevo se añade AQUÍ, que es la pieza que usan ficha, recibos y listas.
  late_fee: 'Pago tardío',
};
/** Color de cada concepto, para que se distingan de un vistazo en listas y recibos.
 *  Son clases de Tailwind ya completas (fondo + texto), no colores sueltos. */
export const TYPE_COLOR: Record<string, string> = {
  membership: 'bg-accent/15 text-accent-ink',
  license: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
  class: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  event: 'bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-400',
  voucher: 'bg-teal-500/15 text-teal-600 dark:text-teal-400',
  voucher_trial: 'bg-teal-500/15 text-teal-600 dark:text-teal-400',
  voucher_voucher: 'bg-teal-500/15 text-teal-600 dark:text-teal-400',
  product: 'bg-orange-500/15 text-orange-600 dark:text-orange-400',
  refund: 'bg-red-500/15 text-red-600 dark:text-red-400',
  late_fee: 'bg-red-500/15 text-red-600 dark:text-red-400',
  other: 'bg-surface-2 text-content-2',
};
export const METHOD_LABEL: Record<string, string> = {
  cash: 'Efectivo', creditcard: 'Tarjeta', wire: 'Transferencia', card: 'Tarjeta', cash_usd: 'Dólares',
  voucher: 'Voucher', other: 'Otro',
};

// Acepta null/undefined a propósito: media app pasa columnas DECIMAL que el API devuelve
// como `string | null` (precio de evento, importes sin capturar). `Number(null)` ya daba 0.
// Sin decimales en TODO el panel (Karla 3-nov-2026, #1635: «aplicar lo del no uso de decimales en todo»). Solo
// presentación: redondea al mostrar; montos y cálculos no cambian. Medido ese día: 0 de 1,040 pagos con centavos.
export function money(n: number | string | null | undefined) {
  return '$' + Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 });
}
/** Pesos ENTEROS para las pantallas de Finanzas y Reportes (Karla 2-nov-2026, #1628: «en finanzas/reportes NO
 *  USAR DECIMALES»). Solo presentación: redondea al mostrar (half-up); montos y cálculos no cambian. Caja, cobros,
 *  recibos y la ficha del alumno siguen con `money` (centavos), porque ahí se cobra el importe exacto. */
export function moneyEntero(n: number | string | null | undefined) {
  return '$' + Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 });
}
export function getDay(date: string) { return api.get<DayResp>(`/finances/day?date=${date}`); }

export interface ReceiptPayment {
  payment_id: number; kydemy_id: number | null; datetime: string; pay_date: string | null;
  type: string; method: string; amount: string; amount_base: string | null; amount_discount: string | null;
  amount_vat: string | null; currency: string | null; months: string | null; assoc_name: string | null;
  /** Cobro en dólares (y mixto: + pesos). `amount` es siempre el total en PESOS. */
  usd_amount?: string | number | null; fx_rate?: string | number | null; mxn_amount?: string | number | null;
  invoice_number: string | null; comment: string | null; payer: string | null; teller: string | null;
  /** Nombre del descuento aplicado al cobro, ej. '-35% descuento'. */
  discount_name: string | null;
}
export interface ReceiptResp { data: ReceiptPayment; studio: { name: string; address: string; prefix: string } }
export function getPayment(id: number) { return api.get<ReceiptResp>(`/finances/payments/${id}`); }
export function getDates() { return api.get<{ data: { pay_date: string; total: string; n: number }[] }>('/finances/dates'); }

/** Desglose de las mensualidades del periodo: por plan (2DI, F60…) y por periodo cubierto
 *  (mensual / trimestral / a medida = las subfilas Monthly/Quarterly/Other Fees de Kydemy). */
export interface DesgloseFila { k: string; label: string; n: number; total: number }
export interface DesgloseMensualidades {
  byPlan: { name: string; code: string | null; color: string | null; n: number; total: string }[];
  byPeriodo: { periodo: 'mensual' | 'trimestral' | 'a_medida'; n: number; total: string }[];
}
export interface MonthResp {
  month: string; prevMonth: string;
  total: number; count: number; prevTotal: number;
  byMethod: { method: string; total: string; n: number }[];
  byType: { type: string; total: string; n: number }[];
  mensualidades?: DesgloseMensualidades;
  otros?: DesgloseFila[];
  clasesPrueba?: DesgloseFila[];
}
export interface WeekResp {
  from: string; to: string; prevFrom: string; prevTo: string;
  total: number; count: number; prevTotal: number;
  byMethod: { method: string; total: string; n: number }[];
  byType: { type: string; total: string; n: number }[];
  byDay: { day: string; total: string; n: number }[];
  mensualidades?: DesgloseMensualidades;
  otros?: DesgloseFila[];
  clasesPrueba?: DesgloseFila[];
}
/** URL del archivo descargable de Ingresos (Excel real o PDF con logo), generado en el servidor
 *  desde la misma consulta que la pantalla. `valor` = fecha (día/semana) o 'YYYY-MM' (mes). */
export function urlExportIngresos(vista: 'dia' | 'semana' | 'mes', valor: string, fmt: 'xlsx' | 'pdf') {
  const ruta = vista === 'dia' ? `day.${fmt}?date=` : vista === 'semana' ? `week.${fmt}?start=` : `month.${fmt}?month=`;
  return `/api/letsmove/finances/${ruta}${encodeURIComponent(valor)}`;
}

/** Semana lunes→domingo que contiene `day` (cualquier día de ella). */
export function getWeek(day: string) { return api.get<WeekResp>(`/finances/week?start=${day}`); }

export function getMonth(month?: string) {
  return api.get<MonthResp>(`/finances/month${month ? `?month=${month}` : ''}`);
}

// ── Captura manual de pagos (F5b) ──
// Cuadritos de tipo (Karla 29-jul, video 15:02: en Kydemy son tiles, no un desplegable).
// Mismo juego de conceptos que ella ya usa; `product` (vitrina) es nuestro añadido.
export const PAY_TYPE_OPTIONS = [
  { v: 'membership', label: 'Mensualidad' },
  { v: 'class', label: 'Clase suelta' },
  { v: 'license', label: 'Inscripción' },
  { v: 'event', label: 'Evento' },
  { v: 'voucher', label: 'Bono de clases' },
  { v: 'voucher_trial', label: 'Clase de prueba' },
  { v: 'product', label: 'Vitrina' },
  { v: 'other', label: 'Otro pago' },
];
/** LOS MISMOS cuadros de tipo en «Registrar pago» y «Registrar deuda», en el mismo orden (Carlos 29-sep-2026:
 *  «el chiste es que ambos deben verse igual»). Fuera Otro/Otra deuda, Bono de clases y Clase de prueba (la
 *  muestra gratis la detecta trial-check dentro de «Clase suelta»). Los tipos viejos solo reaparecen al corregir
 *  un pago que ya los tiene. */
export const TIPOS_COMUNES = [
  { v: 'membership', label: 'Mensualidad' },
  { v: 'class', label: 'Clase suelta' },
  { v: 'license', label: 'Inscripción' },
  { v: 'event', label: 'Evento' },
  { v: 'product', label: 'Vitrina' },
  // «Otro» REGRESA el 2-oct-2026 (Mabel + video de Aracely): un concepto libre que no es mensualidad,
  // inscripción, clase ni vitrina — p. ej. el Powerade que Ximena debía desde Kydemy (no se migró) o
  // «otro conceptito» que a veces pagan. NO toca el inventario (solo 'product' lo mueve) y exige concepto.
  // En reportes cae en «Otros» (lib/letsmoveIngresos.categoriaOtro). Ya no es AyB: eso es Vitrina.
  { v: 'other', label: 'Otro' },
];
export const TIPOS_COLS = 'grid-cols-3';
/** Cuadritos de tipo al dar de alta una DEUDA (sin vitrina ni clase de prueba: no se deben). */
export const DEBT_TYPE_OPTIONS = [
  { v: 'membership', label: 'Mensualidad' },
  { v: 'class', label: 'Clase suelta' },
  { v: 'license', label: 'Inscripción' },
  { v: 'event', label: 'Evento' },
  // «Bono de clases» fuera (Carlos 29-sep-2026): nunca se usó (0 adeudos).
  { v: 'other', label: 'Otra deuda' },
];
// `product` (fiado de vitrina) NO está arriba a propósito: se crea desde la Venta de vitrina, con su
// carrito, no tecleando un importe suelto. Su etiqueta sale de TYPE_LABEL.product = «Vitrina».
export const PAY_METHOD_OPTIONS = [
  { v: 'cash', label: 'Efectivo' },
  { v: 'wire', label: 'Transferencia' },
  { v: 'creditcard', label: 'Tarjeta' },
];

export interface NewPayment {
  /** Concepto de INGRESO del catálogo para un cobro «Otro» (clase privada, renta…). 7-oct-2026. */
  concept_id?: number | null;
  /** Cobro en DÓLARES (1-oct-2026): method 'cash' + currency 'USD' + usd_amount; el servidor calcula `amount`
   *  (pesos) con el tipo de cambio vigente. `fx_rate` = el TC con que se calculó en pantalla (si cambió → 409). */
  currency?: 'USD' | 'MXN';
  usd_amount?: number | null;
  fx_rate?: number | null;
  /** Cobro MIXTO (3-nov-2026): la parte que pagó en PESOS en efectivo dentro del cobro en dólares.
   *  amount = round(usd × TC) + mxn_amount; en la caja esos pesos cuentan como efectivo en pesos. */
  mxn_amount?: number | null;
  /** Clase suelta / muestra: a qué clase y día va → sale en «Pasar lista» sin inscribirse (F3). */
  class_id?: number | null;
  class_date?: string | null;
  /** Descuento del catálogo aplicado a ESTE cobro; el servidor calcula el importe descontado. */
  discount_id?: number | string | null;
  account_id?: number | null;
  payer_name?: string | null;
  /** Comprador = MAESTRO o alguien del equipo (usuario, no alumno). Excluyente con `account_id`. */
  payer_user_id?: number | null;
  type: string;
  method: string;
  amount: number;
  pay_date: string;
  months?: string | null;
  assoc_name?: string | null;
  comment?: string | null;
  is_partial?: boolean;
  total_expected?: number | null;
  /** F2: abono a un adeudo existente (el backend valida saldo y auto-salda al cubrirlo). */
  debt_id?: number | null;
  /** Vitrina: id del producto del catálogo core (valida existencia y autollenar concepto). */
  product_id?: number | null;
  /** Vitrina: unidades vendidas (dato real p/ reportes; NULL = 1). */
  qty?: number | null;
}
export function createPayment(p: NewPayment) {
  return api.post<{ ok: true; payment_id: number; pay_date: string; is_partial?: boolean; remaining?: number; debt_id?: number | null;
    abono_debt_id?: number | null; debt_remaining?: number | null; debt_settled?: boolean; amount?: number }>('/finances/payments', p);
}
export function deletePayment(id: number) {
  return api.del<{ ok: true }>(`/finances/payments/${id}`);
}
/** Corregir un pago capturado a mano. Solo los locales (kydemy_id NULL). */
export interface PaymentPatch {
  /** Concepto de INGRESO del catálogo para un cobro «Otro» (clase privada, renta…). 7-oct-2026. */
  concept_id?: number | null;
  type?: string; method?: string; amount?: number; pay_date?: string;
  months?: string | null; assoc_name?: string | null; comment?: string | null;
  /** Cobro en dólares: corregir los USD (recalcula con su TC) · currency 'MXN' = pasarlo a pesos. */
  usd_amount?: number; currency?: 'MXN';
  /** Cobro mixto: la parte en pesos de un cobro en dólares (null = sin pesos). */
  mxn_amount?: number | null;
}
export function updatePayment(id: number, patch: PaymentPatch) {
  return api.patch<{ ok: true; unchanged?: boolean; debt_id: number | null; debt_settled: number | null; cashbox_closed?: boolean }>(
    `/finances/payments/${id}`, patch);
}

// ── Gastos (egresos) — F5b ──
export interface Expense {
  expense_id: number; kydemy_id: number | null; datetime: string;
  amount: string; category: string | null; notes: string | null;
  concept_id?: number | null; concept_name?: string | null; concept_category?: string | null; concept_section?: string | null;
  iva_pct?: string | null; iva_amount?: string | null;
  /** Fijo/Variable (7-oct-2026): 1 fijo · 0 variable · null sin marcar · undefined = API vieja. */
  is_fixed?: number | null;
}
export interface ExpensesResp {
  range?: 'day' | 'week' | 'month'; month?: string | null; from?: string | null; to?: string | null;
  data: Expense[]; total: number; count: number;
  byCategory: { category: string; total: string; n: number }[];
}
export const EXPENSE_CATEGORIES = [
  'Renta', 'Maestros / Nómina', 'Servicios (luz/agua/internet)', 'Mantenimiento',
  'Insumos', 'Publicidad', 'Vestuario', 'Música', 'Eventos', 'Otros',
];
export function getExpenses(month?: string) {
  return api.get<ExpensesResp>(`/finances/expenses${month ? `?month=${month}` : ''}`);
}
/** Gastos por rango (día/semana/mes) anclados a una fecha (default hoy). */
export function getExpensesRange(range: 'day' | 'week' | 'month', date?: string) {
  const q = new URLSearchParams({ range });
  if (date) q.set('date', date);
  return api.get<ExpensesResp>(`/finances/expenses?${q.toString()}`);
}

// ── Vista general (overview) ──
export interface FinanceOverview {
  today: { income: number; income_count: number; expense: number; expense_count: number; net: number };
  month: { income: number; income_count: number; expense: number; expense_count: number; net: number };
  debts: { total_open: number; count_open: number; students_open: number; overdue_total: number; overdue_count: number };
  cashbox:
    | { status: 'none' }
    | { status: 'open'; expected_amount: number; opened_at: string }
    | { status: 'closed'; closed_at: string; expected_amount: number; close_amount: number; difference: number; semaforo: 'ok' | 'warn' | 'bad' };
}
export function getFinanceOverview() { return api.get<FinanceOverview>('/finances/overview'); }

// ── Nómina de maestros (por HORARIO, 15-sep-2026) ──
// Se paga cada sesión PROGRAMADA (haya lista o no). `has_list=false` es aviso de recepción.
// Excepciones por sesión: not_given (0 h) / substitute (cobra el suplente con su tarifa).
export type PayMode = 'hourly' | 'commission';
export interface PayrollSession {
  date: string; class_id: number; class_name: string; start_time: string | null;
  scheduled: boolean; has_list: boolean; hours: number; rate: number | null; pay: number;
  exception_id: number | null; kind: 'not_given' | 'substitute' | null; note: string | null;
  marked_by?: string | null;  // quién marcó la excepción (Nómina o la casilla de Pasar lista)
  substitute_name?: string;   // en la fila del titular: quién la dio
  substitute_for?: string;    // en la fila del suplente: a quién suplió
}
export interface PayrollTeacher {
  teacher_id: number; teacher_name: string; has_photo: number;
  pay_mode: PayMode; commission_pct: number | null;
  rate: number | null; morning_rate: number | null; has_rate: boolean; total_hours: number; pay: number | null;
  // Sueldo FIJO semanal (staff por persona, 3-oct-2026). `salary` = lo que paga el periodo (semana ×1, mes × sus lunes);
  // `pay` ya lo incluye. Dayan combina clases por hora + sueldo fijo.
  salary_weekly: number | null; salary: number; has_salary: boolean; weeks?: number;
  missing_lists: number; not_given: number; substituted: number;
  commission?: { month: string; partial: boolean; base: number; delivered: number; pending: number; items: number; month_base: number; month_commission: number; month_pending: number };
  sessions: PayrollSession[]; paid?: boolean; run_id?: number | null;
  // Líneas manuales del periodo (bono puntualidad / desempeño / comisión / sueldo extra / otro). Cada una YA es un
  // gasto propio; no entran en `pay` (lo que convierte «Registrar pago»). `solo_extras` = persona sin clases ni
  // sueldo este periodo que solo tiene líneas manuales.
  extras: PayrollExtra[]; extras_total: number; solo_extras?: boolean;
  // Ajustes de la semana (B4, 15-oct-2026): concepto + monto con signo antes de «Registrar pago». `adjustments_total` =
  // los que se suman a `pay` (los bonos ya pagados se ven como línea de bono en `extras`); `pay_adjusted` = pay + eso.
  adjustments?: PayrollAdjustment[]; adjustments_total?: number; adjustments_pending?: number; pay_adjusted?: number | null;
}
export interface PayrollAdjustment {
  id: number; user_id: number; period_from: string; period_to: string; concept: string; concept_label: string; label: string | null;
  nombre: string; amount: number; signo: 1 | -1; note: string | null; included: boolean; run_id: number | null; extra_id: number | null;
  es_bono: boolean; estado: 'pendiente' | 'pagado' | 'incluido'; created_at: string | null; created_by_name: string | null;
}
export interface AdjustmentConcept { key: string; label: string; signo: 1 | -1; es_bono: boolean; pide_texto: boolean }
export function getAdjustmentConcepts() {
  return api.get<{ disponible: boolean; data: AdjustmentConcept[] }>('/finances/payroll/adjustments/concepts');
}
export function addPayrollAdjustment(body: { user_id: number; date: string; concept: string; amount: number; label?: string; note?: string; confirm?: boolean; included?: boolean }) {
  return api.post<{ ok: true; adjustment: PayrollAdjustment; calculated: number; adjusted: number }>('/finances/payroll/adjustments', body);
}
export function deletePayrollAdjustment(id: number) {
  return api.del<{ ok: true }>(`/finances/payroll/adjustments/${id}`);
}
/** Concepto del «monto editado» (Mabel 19-oct-2026): UNO por persona y semana, monto = nuevo − calculado. */
export const MONTO_DIRECTO = 'monto_directo';
/** Editar el monto a pagar de la semana (solo semanas sin pagar). `reset` = volver al calculado. */
export function setPayrollDirectAmount(body: { user_id: number; date: string; amount?: number; note?: string; reset?: boolean; confirm?: boolean }) {
  return api.put<{ ok: true; calculated: number; amount: number; delta: number; edited: boolean; adjustment: PayrollAdjustment | null }>('/finances/payroll/adjustments/direct', body);
}
export type ExtraConcept = 'bono_puntualidad' | 'bono_desempeno' | 'comision' | 'sueldo_extra' | 'otro';
export const EXTRA_CONCEPTS: Record<ExtraConcept, string> = { bono_puntualidad: 'Bono puntualidad', bono_desempeno: 'Bono desempeño', comision: 'Comisión', sueldo_extra: 'Sueldo extra', otro: 'Otro' };
export interface PayrollExtra {
  id: number; concept: ExtraConcept | 'bono_navideno'; concept_label: string; label: string | null; amount: number; note: string | null;
  from_adjustment?: boolean;   // nació de un ajuste de la semana (B4): se quita deshaciendo el pago
  expense_id: number | null; created_at: string; created_by_name: string | null;
}
export function addPayrollExtra(body: { user_id: number; range: 'day' | 'week' | 'month'; date: string; concept: ExtraConcept; label?: string; amount: number; note?: string; confirm?: boolean }) {
  return api.post<{ ok: true; id: number; expense_id: number; amount: number }>('/finances/payroll/extras', body);
}
export function deletePayrollExtra(id: number) {
  return api.del<{ ok: true }>(`/finances/payroll/extras/${id}`);
}
// Historial de nómina de una persona (pestaña «Nómina» de la ficha de maestro/staff, 3-oct-2026).
export interface PayrollHistory {
  user: { id: number; name: string; es_profesor: boolean }; from: string; months: number;
  runs: { id: number; period_from: string; period_to: string; hours: number | null; rate: number | null; amount: number; salary: number | null; classes: number; expense_id: number | null; created_at: string; created_by_name: string | null }[];
  extras: { id: number; period_from: string; period_to: string; concept: ExtraConcept; concept_label: string; label: string | null; amount: number; note: string | null; expense_id: number | null; created_at: string; created_by_name: string | null }[];
  payouts: { id: number; payment_id: number; amount: number; delivered_at: string; note: string | null; alumna: string | null; created_at: string; created_by_name: string | null }[];
  totals: { runs: number; extras: number; payouts: number; total: number };
}
export function getPayrollHistory(userId: number, months = 12) {
  return api.get<PayrollHistory>(`/finances/payroll/history/${userId}?months=${months}`);
}
export interface PayrollResp {
  range: 'day' | 'week' | 'month'; from: string; to: string;
  teachers: PayrollTeacher[];
  totals: { pay: number; hours: number; extras: number; adjustments?: number; total: number; teachers: number; sessions: number; missing_rates: number; paid_count?: number };
  adjustments_enabled?: boolean;   // B4: vista Semana y migración corrida → se pueden capturar ajustes
  // Semana abierta (Mabel 9-oct-2026): null si el negocio no la usa o el rango no es semana.
  payroll_week?: { open_week: string; is_open: boolean; pending: { id: number; name: string }[]; can_reopen: boolean } | null;
}
export function getPayroll(range: 'day' | 'week' | 'month', date?: string) {
  const q = new URLSearchParams({ range });
  if (date) q.set('date', date);
  return api.get<PayrollResp>(`/finances/payroll?${q.toString()}`);
}
export function payPayroll(account_id: number, range: 'day' | 'week' | 'month', date: string, confirm = false) {
  return api.post<{ ok: true; run_id: number; expense_id: number; amount: number; total?: number; adjustments?: number }>('/finances/payroll/pay', { account_id, range, date, confirm });
}
export function closePayrollWeek(force = false) {
  return api.post<{ ok: true; open_week: string; closed: string; pending: { id: number; name: string }[] }>('/finances/payroll/close-week', { force });
}
export function reopenPayrollWeek() {
  return api.post<{ ok: true; open_week: string }>('/finances/payroll/reopen-week', {});
}
export function undoPayroll(runId: number) {
  return api.del<{ ok: true }>(`/finances/payroll/pay/${runId}`);
}
export function setPayrollException(body: { class_id: number; date: string; kind: 'not_given' | 'substitute'; substitute_user_id?: number; note?: string }) {
  return api.post<{ ok: true }>('/finances/payroll/exceptions', body);
}
export function deletePayrollException(id: number) {
  return api.del<{ ok: true }>(`/finances/payroll/exceptions/${id}`);
}
export interface TeacherRate {
  account_id: number; name: string; has_photo: number; hourly_rate: string;
  morning_rate: string | null; morning_until: string; pay_mode: PayMode; commission_pct: string | null;
  salary_weekly: string | null;   // sueldo fijo semanal (null = no tiene)
  es_profesor: number;            // 0 = staff que no da clases (solo sueldo fijo)
}
export function getTeacherRates() { return api.get<{ data: TeacherRate[] }>('/finances/teacher-rates'); }
/** Solo se pisan los campos que se mandan (la ficha del maestro manda solo hourly_rate). */
export function setTeacherRate(id: number, hourly_rate: number, extra?: { pay_mode?: PayMode; morning_rate?: number | null; morning_until?: string; commission_pct?: number | null; salary_weekly?: number | null }) {
  return api.put<{ ok: true; account_id: number; hourly_rate: number }>(`/finances/teacher-rates/${id}`, { hourly_rate, ...(extra || {}) });
}
// Hoja de comisión (profes pay_mode='commission'): base del mes por alumna y entregas pago por pago.
export interface CommissionItem {
  payment_id: number; contact_id: number; nombre: string; is_active: boolean; clases: string; plan: string | null;
  concept: 'mensualidad' | 'curso' | 'inscripcion' | 'multa' | 'clase_suelta'; amount: number; pay_date: string;
  method: string | null; months: string | null; comment: string | null; commission: number | null;
  payout_id: number | null; delivered_at: string | null; payout_amount: number | null;
}
export interface CommissionResp {
  teacher_id: number; teacher_name: string; pct: number | null; month: string; items: CommissionItem[];
  base: number; commission: number; delivered: number; pending: number;
}
export function getCommission(userId: number, month: string) {
  return api.get<CommissionResp>(`/finances/payroll/commission/${userId}?month=${month}`);
}
export function deliverCommission(user_id: number, payment_ids: number[], delivered_at?: string, note?: string) {
  return api.post<{ ok: true; delivered: number }>('/finances/payroll/commission/deliver', { user_id, payment_ids, delivered_at, note });
}
export function undoCommissionDelivery(id: number) {
  return api.del<{ ok: true }>(`/finances/payroll/commission/deliver/${id}`);
}

// ── Comisión POR ENTREGAR desde Caja (Mabel 5-oct-2026: quitar el Excel de comisiones de Noli) ──
export interface CommissionPendingItem {
  payment_id: number; nombre: string; concept: string; amount: number; commission: number; pay_date: string; month: string; clases?: string;
}
export interface CommissionDelivery { id: number; amount: number; delivered_at: string; note: string | null; by_name: string | null; n: number; caja_abierta: boolean }
export interface CommissionPending {
  user_id: number; name: string; pct: number; items: CommissionPendingItem[]; base: number; pending: number; deliveries: CommissionDelivery[];
}
export function getCommissionPending() { return api.get<{ data: CommissionPending[] }>('/finances/commission/pending'); }
/** Retiro de caja + gasto «Nómina / Comisión» + pagos marcados como entregados, en un paso. */
export function entregarComision(user_id: number, payment_ids: number[]) {
  return api.post<{ ok: true; delivery_id: number; amount: number; n: number }>('/finances/commission/entregar', { user_id, payment_ids });
}
export function deshacerEntregaComision(id: number) { return api.del<{ ok: true }>(`/finances/commission/entregar/${id}`); }
export function getExpenseMonths() {
  return api.get<{ data: { month: string; total: string; n: number }[] }>('/finances/expenses/months');
}
export function createExpense(e: { amount: number; date: string; category?: string; concept_id?: number | null; iva_pct?: number | null; notes?: string | null; is_fixed?: boolean }) {
  return api.post<{ ok: true; expense_id: number; month: string }>('/finances/expenses', e);
}
export function deleteExpense(id: number) {
  return api.del<{ ok: true }>(`/finances/expenses/${id}`);
}

// ── Adeudos (debts) — F5b ──
export interface Debt {
  // OJO port lealify-base (28-jul): el API devuelve contact_id (alumno = contacts), no account_id.
  debt_id: number; contact_id: number | null; debt_type: string | null;
  amount: string; month: string | null; membership_name: string | null;
  comment: string | null; settled: number; source: string;
  /** F2: abonado acumulado y saldo restante (amount − abonos). */
  paid_amount: string; remaining: string;
  created_at: string | null; due_date: string | null; days_to_due: number | null;
  created_by_name: string | null;
  student_name: string; has_photo: number;
  /** Fiado de vitrina a un maestro/equipo: no hay alumno, `student_name` trae su nombre. */
  payer_user_id?: number | null; es_maestro?: number;
  /** 1 = quien fía es PROFESOR (tiene ficha en /profesores). Gerencia/recepción no: sin enlace. */
  payer_es_profe?: number | null;
  /** 'desfasado' = resto de la mensualidad que se cobra en SU fecha (10-oct-2026); 'cycle_day:N', etc. */
  due_rule_applied?: string | null;
  phone_prefix?: string | null; phone_number?: string | null;
}
/** A quién del equipo se le puede cobrar o fiar en vitrina, con lo que ya debe de fiados. */
/** `kind`: 'staff' (Karla, Dayan, Mabel, Aracely — ajuste `vitrina_equipo`) · 'maestro' (da clase o tiene el
 *  puesto) · 'publico' (solo con `todos=1`: quien no es ni uno ni otro compra como público general). */
export interface StaffBuyer { user_id: number; name: string; debe: number; kind: 'staff' | 'maestro' | 'publico'; es_profe?: boolean }
/** `discount_pct`: el descuento del equipo (staff y maestros) al comprar o fiar en vitrina. */
export function getStaffBuyers(todos = false) { return api.get<{ data: StaffBuyer[]; discount_pct: number }>(`/finances/buyers/staff${todos ? '?todos=1' : ''}`); }
export interface DebtsResp {
  data: Debt[];
  summary: { total_open: number; count_open: number; students_open: number };
}
/** Alumnas que ya no están (baja o sin clase vigente) y conservan cargos vivos.
 *  No se ven en `getDebts` ni en «Por cobrar»: esas dos filtran por alumna activa, así que el
 *  adeudo de quien se dio de baja desaparece de la vista SIN dejar de existir (y reaparece si
 *  alguien reactiva su ficha). Esta es la única vista que mira del otro lado del filtro. */
export interface BajaConCargo extends Omit<Debt, 'days_to_due' | 'created_by_name'> {
  is_active: number;
  /** Clases a las que ASISTIÓ de verdad en el mes que se le cobra. 0 = no pisó el estudio. */
  attended_in_month: number;
  last_attendance: string | null;
  last_payment: string | null;
}
export interface BajasResp {
  data: BajaConCargo[];
  summary: { total: number; count: number; students: number; students_without_attendance: number };
}
export function getBajasConCargo(month?: string) {
  return api.get<BajasResp>(`/finances/debts/bajas${month ? `?month=${month}` : ''}`);
}

export function getDebts(includeSettled = false) {
  return api.get<DebtsResp>(`/finances/debts${includeSettled ? '?include_settled=1' : ''}`);
}
/** Deudas de UN alumno (bloque de su ficha). */
export function getMemberDebts(contactId: number, includeSettled = false) {
  return api.get<DebtsResp>(`/finances/debts?contact_id=${contactId}${includeSettled ? '&include_settled=1' : ''}`);
}
/** Fiados de UN maestro / persona del equipo (bloque de su ficha de profesor). */
export function getStaffDebts(userId: number, includeSettled = false) {
  return api.get<DebtsResp>(`/finances/debts?payer_user_id=${userId}${includeSettled ? '&include_settled=1' : ''}`);
}
/** Con `payer_user_id` en vez de `account_id` el adeudo es el FIADO de un maestro (solo vitrina). */
/** `lines` (solo fiado de vitrina): qué artículos salieron, para descontarlos de existencias. */
export function createDebt(d: { account_id?: number | null; payer_user_id?: number | null; amount: number; month?: string | null; due_date?: string | null; debt_type?: string; comment?: string | null; lines?: { product_id: number; qty: number }[]; desfasado?: boolean }) {
  return api.post<{ ok: true; debt_id: number }>('/finances/debts', d);
}
/** Cancela el adeudo guardando el motivo (no lo borra: queda para revisarlo después).
 *  Sólo funciona con adeudos nuestros; los de Kydemy responden 403 porque volverían en el delta. */
export function cancelDebt(id: number, reason: string) {
  return api.post<{ ok: true }>(`/finances/debts/${id}/cancel`, { reason });
}
export function patchDebtSettled(id: number, settled: boolean) {
  return api.patch<{ ok: true; settled: number }>(`/finances/debts/${id}`, { settled });
}
export function deleteDebt(id: number) {
  return api.del<{ ok: true }>(`/finances/debts/${id}`);
}
// F2: deuda GRUPAL — misma deuda a todos los alumnos activos de una clase (vestuario).
export interface BulkDebtResp { ok?: true; dry_run?: boolean; class_name: string; count: number; total: number; students?: string[] }
export function createBulkDebt(p: { class_id: number; amount: number; concept: string; due_date?: string | null; dry_run?: boolean }) {
  return api.post<BulkDebtResp>('/finances/debts/bulk', p);
}
// Multa por pago tardío (Karla 28-jul: $100 a partir del día 4; la mensualidad vence el día 3).
// El backend simula por defecto: sin `dry_run: false` no crea nada.
export interface LateFeeStudent {
  contact_id: number; debt_id: number; nombre: string; month: string | null; plan?: string | null;
  due_date?: string | null; dias_atraso: number; debe?: number; multa?: number; amount?: number;
  /** 0 = activa, con adeudo y sin ninguna clase → casi siempre es una baja que nadie registró
   *  (su clase se canceló), no una morosa. La pantalla la trae desmarcada. */
  clases_activas?: number;
}
export interface LateFeeResp {
  ok?: true; dry_run?: boolean; month: string; fee_amount: number;
  count?: number; created?: number; total: number; criterio?: string;
  already: { count: number; total: number }; students?: LateFeeStudent[];
  /** Vencidos que NO se multan porque su fecha de vencimiento no cae en su propio mes
   *  (due_date heredado del lote con el que se creó) — hay que corregirlos a mano. */
  descartados_por_fecha?: { debt_id: number; nombre: string; month: string | null; due_date: string; amount: number }[];
}
export function applyLateFees(p: { month?: string; amount?: number; grace_until?: string | null; exclude_contact_ids?: number[]; dry_run?: boolean }) {
  return api.post<LateFeeResp>('/finances/debts/late-fees', p);
}

// ── Caja / arqueo (cashbox) — F5b ──
export interface CashboxMovement {
  movement_id: number; kind: 'withdrawal' | 'deposit'; amount: string; usd?: string | null;
  reason: string | null; created_at: string; teller: string | null;
}
export interface TurnoPayment {
  payment_id: number; hora: string; type: string; method: string; amount: string; name: string | null;
  qty?: number | null;
  /** 1 = cobro hecho con la caja cerrada que esta caja tomó al abrir. */
  adoptado?: number | null;
}
export interface OpenCashbox {
  cashbox_id: number; opened_at: string; open_amount: string;
  opened_by_name: string; cash_collected: number; num_payments: number; expected_amount: number;
  movements?: CashboxMovement[]; movements_net?: number;
  /** Cobros del turno (todos los métodos), para verlos sin salir de la Caja. */
  payments?: TurnoPayment[];
  shift_label?: string | null; fecha?: string | null;
  /** Aviso de apertura: lo que dejó el cierre anterior y la diferencia contra el conteo. */
  open_expected?: string | number | null; open_difference?: string | number | null;
  /** Conteo de billetes con que se abrió (JSON). Vacío si se abrió tecleando solo el importe. */
  open_breakdown?: string | null;
  /** Fiado del turno que sigue sin pagarse. El de ALUMNAS ya va sumado en `expected_amount`
   *  (al cierre ese dinero tiene que estar); el de maestros se acumula y no suma. */
  fiado_alumnas?: number; fiado_maestros?: number;
  /** Efectivo de cobros hechos con la caja cerrada que esta caja tomó al abrir: ya va dentro del
   *  conteo de apertura, por eso NO entra en `cash_collected` ni se suma otra vez al esperado. */
  adoptado_efectivo?: number; adoptados_n?: number;
  /** Dólares del turno (se cuadran aparte de los pesos). null = la caja no maneja dólares. */
  usd?: { expected: number; collected: number; n: number; open: number | null } | null;
  fx_rate?: number;
}

/** Lo que dejó el último corte cerrado — para el aviso de falta/sobra AL ABRIR. */
export interface LastClose {
  cashbox_id: number; close_amount: string; closed_at: string | null; shift_label: string | null;
  difference: string | null; fecha: string | null; closed_by_name: string | null;
  /** Billetes con que CERRÓ ese turno: si se abre con ese mismo importe, son los mismos billetes. */
  close_breakdown?: string | null;
  /** Dólares contados al cerrar (null = esa caja no manejó dólares). */
  close_usd?: string | number | null;
}
/** Cobro registrado con la caja CERRADA (1-oct-2026, audio de Mabel): la siguiente caja que se abra lo adopta. */
export interface CobroCajaCerrada {
  payment_id: number; fecha: string; hora: string; type: string; method: string; amount: number;
  teller: string | null; name: string | null;
  currency?: string | null; usd_amount?: number | null; mxn_amount?: number | null;
}
export function getCashboxLastClose() {
  return api.get<{ last: LastClose | null; pendientes?: CobroCajaCerrada[]; pendiente_efectivo?: number; pendiente_usd?: number; fx_rate?: number }>('/finances/cashbox/last-close');
}

// ── TIPO DE CAMBIO del dólar (ajuste fx_usd, 1-oct-2026). Lo ve la caja; lo cambian Karla/Mabel. ──
export async function getTipoCambio(): Promise<number> {
  const r = await api.get<{ value: string }>('/settings/fx_usd');
  try { const o = JSON.parse(r.value); return Number(o.rate) || 17; } catch { return 17; }
}
export function putTipoCambio(rate: number) {
  return api.put<{ value: string }>('/settings/fx_usd', { value: JSON.stringify({ rate }) });
}
/** Pesos de un cobro en dólares: redondeado a peso (como el servidor). */
export const pesosDeDolares = (usd: number, tc: number) => Math.round((Number(usd) || 0) * (Number(tc) || 0));
/** «50 USD» o, en un cobro MIXTO (3-nov-2026), «50 USD + $100». */
export const textoDolares = (usd: number | string | null | undefined, mxn?: number | string | null) =>
  `${Number(usd) || 0} USD${Number(mxn) > 0 ? ` + ${money(Number(mxn))}` : ''}`;

/** Un renglón de los movimientos del turno con saldo corrido (columna «Acumulado»).
 *  `acumulado` viene NULL en los cobros que no son en efectivo: no mueven el cajón. */
export interface LedgerRow {
  at: string; kind: 'open' | 'payment' | 'deposit' | 'withdrawal' | 'close' | 'debt';
  label: string; detail: string | null; teller: string | null; name: string | null;
  method: string; amount: number; afecta_efectivo: boolean; acumulado: number | null;
  payment_id?: number;
  /** Cobro en dólares: USD y, si fue mixto, los pesos; `efectivo` = lo que movió el cajón de PESOS. */
  currency?: string; usd_amount?: number; mxn_amount?: number | null; efectivo?: number;
  /** Solo en `kind: 'debt'` (fiado del turno): el adeudo, lo que de él sigue sin pagarse y si el
   *  deudor es del equipo (a maestros no se les suma al cajón). */
  debt_id?: number; remaining?: number; es_maestro?: boolean;
  /** Para las fotos de la tabla (avatares de usuario y de contacto). */
  teller_id?: number | null; contact_id?: number | null;
  /** Maestro comprador (solo si tiene ficha de profesor) y si el cajero la tiene: enlaces al perfil. */
  payer_user_id?: number | null; teller_es_profe?: boolean;
}
/** Pie del corte: las 4 pastillas de Kydemy + la diferencia. */
export interface LedgerTotales {
  esperado: number | null; contado: number | null;
  entradas: number; salidas: number; diferencia: number | null;
  /** Fiado en el turno y lo que de eso sigue pendiente. Informativo: no es dinero del cajón. */
  fiado?: number; fiado_pendiente?: number; fiados_n?: number;
  /** Del pendiente, cuánto es de alumnas (va en el esperado) y cuánto de maestros (no). */
  fiado_alumnas?: number; fiado_maestros?: number;
}

/** El fiado guarda «Vitrina: 2× Coca…»; donde ya hay etiqueta «Vitrina» al lado, el prefijo sobra
 *  (Carlos 29-sep: «¿no está repitiendo la palabra vitrina?»). */
export function sinPrefijoVitrina(texto?: string | null): string | null {
  if (!texto) return texto ?? null;
  // «Vitrina (−20% equipo): 2× Coca» → «2× Coca · −20% equipo» (el descuento no se pierde).
  const m = texto.match(/^\s*vitrina\s*\(([^)]*)\)\s*:\s*(.*)$/i);
  if (m) return `${m[2]} · ${m[1]}`;
  return texto.replace(/^\s*vitrina\s*:\s*/i, '');
}
export interface LedgerResp {
  box: null | {
    cashbox_id: number; status: string; opened_at: string; closed_at: string | null; shift_label: string | null;
    source?: string; open_amount: number; open_expected: number | null; open_difference: number | null;
    close_amount: number | null; expected_amount: number | null; difference: number | null;
    num_payments?: number | null; total_payments?: number | null;
    fecha?: string | null; notes?: string | null;
    opened_by?: number | null; closed_by?: number | null;
    opened_by_name?: string | null; closed_by_name?: string | null;
    /** Conteo de billetes guardado en la apertura y en el cierre (JSON). */
    open_breakdown?: string | null; close_breakdown?: string | null;
  };
  rows: LedgerRow[]; acumulado_final: number; totales: LedgerTotales | null;
}
export function getCashboxLedger(cashboxId?: number) {
  return api.get<LedgerResp>(`/finances/cashbox/ledger${cashboxId ? `?cashbox_id=${cashboxId}` : ''}`);
}
export interface CashboxRow {
  cashbox_id: number; opened_at: string; closed_at: string | null;
  open_amount: string | null; expected_amount: string | null; close_amount: string | null;
  difference: string | null; num_payments: number | null; total_payments: string | null; source: string;
  shift_label?: string | null; fecha?: string | null;
  /** 'open' = la caja del turno en curso; la lista de días la marca «En curso» (como Kydemy). */
  status?: 'open' | 'closed';
  opened_by?: number | null; closed_by?: number | null;
  opened_by_name?: string | null; closed_by_name?: string | null;
  open_breakdown?: string | null; close_breakdown?: string | null; notes?: string | null;
}
/** Meses con cajas, para el selector de la columna de días. */
export interface CashboxMonth { ym: string; n: number }
// Respuesta del close ampliada (F1): datos suficientes para armar el corte por WhatsApp.
export interface CloseCashboxResp {
  ok: true; cashbox_id: number; expected_amount: number; close_amount: number; difference: number;
  open_amount: number; cash_collected: number; movements_net: number;
  /** Dólares del turno al cerrar (1-oct-2026). null = la caja no manejó dólares. */
  usd?: { expected: number; close: number; difference: number; collected: number; n: number } | null;
  /** Fiado pendiente al cerrar: el de alumnas ya está dentro de `expected_amount`. */
  fiado_alumnas?: number; fiado_maestros?: number;
  movements: { kind: 'withdrawal' | 'deposit'; amount: number; reason: string | null }[];
  methods: { cash: number; wire: number; creditcard: number; other: number; num_payments: number; usd?: number };
  /** Ventas de vitrina (type='product') del turno. */
  vitrina?: { total: number; n: number; units?: number };
  notes: string | null; shift_label: string | null; fecha: string | null; closed_by_name: string | null;
}
export function getCashboxCurrent() { return api.get<{ open: OpenCashbox | null }>('/finances/cashbox/current'); }
export function openCashbox(open_amount: number, breakdown?: string, shift?: { shift_type?: string; shift_label?: string; fecha?: string }) {
  return api.post<{ ok: true; cashbox_id: number; open_difference?: number | null; adoptados?: CobroCajaCerrada[]; adoptado_efectivo?: number }>('/finances/cashbox/open', { open_amount, breakdown, ...(shift || {}) });
}
export function closeCashbox(close_amount: number, notes?: string, breakdown?: string) {
  return api.post<CloseCashboxResp>('/finances/cashbox/close', { close_amount, notes, breakdown });
}
// ── Config de caja (turnos + corte por WhatsApp) — F1 ──
export interface CashboxConfig {
  openingTime: string; closingTime: string; workingDays: number[];
  cashShiftSplit: string | null;
  corte: { enabled: boolean; phone: string; apertura: boolean };
  isDefault?: boolean;
}
export function getCashboxConfig() { return api.get<CashboxConfig>('/finances/cashbox/config'); }
export function putCashboxConfig(cfg: Omit<CashboxConfig, 'isDefault'>) {
  return api.put<CashboxConfig>('/finances/cashbox/config', cfg);
}
export function getCashboxHistory(limit = 40) {
  return api.get<{ data: CashboxRow[]; months: CashboxMonth[] }>(`/finances/cashbox/history?limit=${limit}`);
}
/** Todas las cajas de un mes (incluida la abierta) — la columna de días del rediseño. */
export function getCashboxMonth(ym: string) {
  return api.get<{ data: CashboxRow[]; months: CashboxMonth[] }>(`/finances/cashbox/history?month=${ym}`);
}
/** Botón «Actualizar»: trae de Kydemy las cajas y los cobros del día (~4 s). Devuelve lo que
 *  entró DE VERDAD (contado contra la BD, no lo que dice el log del ETL). */
export interface SyncResp {
  ok: true; segundos: number; cajas: number; cajas_nuevas: number; pagos_nuevos: number;
}
export function syncKydemy() { return api.post<SyncResp>('/finances/sync-kydemy', {}); }

/** URL de descarga del CSV (Excel) de un corte o de un mes. La abre el navegador. */
export function cashboxExportUrl(q: { cashbox_id?: number; month?: string }) {
  const p = q.cashbox_id ? `cashbox_id=${q.cashbox_id}` : `month=${q.month}`;
  return `/api/letsmove/finances/cashbox/export?${p}`;
}
export function addCashboxMovement(kind: 'withdrawal' | 'deposit', amount: number, reason?: string) {
  return api.post<{ ok: true; movement_id: number }>('/finances/cashbox/movement', { kind, amount, reason });
}
/** «Entraron dólares» (10-oct-2026): pagaron en dólares algo registrado en pesos → cambio de divisa en el
 *  cajón (salen usd × TC en pesos, entran los USD). El servidor calcula los pesos; si el TC cambió → 409. */
/** Retiro de DÓLARES del cajón (10-oct-2026): no mueve pesos; tope = lo que la caja espera en USD. */
export function addSalidaDolares(usd: number, reason?: string) {
  return api.post<{ ok: true; movement_id: number }>('/finances/cashbox/movement', { kind: 'usd_out', usd, reason });
}
export function addEntradaDolares(usd: number, fx_rate: number, reason?: string) {
  return api.post<{ ok: true; movement_id: number; amount: number; usd: number }>('/finances/cashbox/movement', { kind: 'usd_in', usd, fx_rate, reason });
}

// ── Gastos PROGRAMADOS / recurrentes (pedido de Karla) ──
export interface Recurring {
  id: number; name: string; amount: string; category: string | null;
  day_of_month: number; active: number; notes: string | null;
  paid_this_month: boolean; due: boolean;
  concept_id?: number | null; concept_name?: string | null; iva_pct?: string | null;
}
export interface RecurringResp {
  data: Recurring[];
  summary: { monthly_total: number; pending_total: number };
}
export function getRecurring() { return api.get<RecurringResp>('/finances/recurring'); }
export function createRecurring(r: { name: string; amount: number; category?: string | null; concept_id?: number | null; iva_pct?: number | null; day_of_month: number; notes?: string | null }) {
  return api.post<{ ok: true; id: number }>('/finances/recurring', r);
}
export function patchRecurring(id: number, patch: Record<string, unknown>) {
  return api.patch<{ ok: true }>(`/finances/recurring/${id}`, patch);
}
export function deleteRecurring(id: number) { return api.del<{ ok: true }>(`/finances/recurring/${id}`); }
export function payRecurring(id: number) { return api.post<{ ok: true }>(`/finances/recurring/${id}/pay`); }

// ── Pronóstico financiero (pedido de Karla) ──
export interface Forecast {
  month: string; expected_income: number; students_billed: number;
  collected: number; collected_count: number; pending_to_collect: number;
  planned_expenses: number; real_expenses: number; projected_profit: number;
  month_progress: number; is_current: boolean;
}
export function getForecast(month?: string) {
  return api.get<Forecast>(`/finances/forecast${month ? `?month=${month}` : ''}`);
}

// ── Presupuesto vs Real (Reportes › Pronóstico; el reporte mensual de gestión para Mabel, 26-sep-2026) ──
// Lo arma el servidor en lib/letsmovePresupuesto.js: la misma fuente que el Excel y el PDF.
export interface PresupuestoNT { n: number; total: number }
export interface PresupuestoFila {
  k: string; categoria: string; concepto: string; presupuesto: number | null; real: number; n: number;
  variacion: number | null; pct: number | null; origen: string; referencia: number | null;
}
export interface PresupuestoProfe {
  user_id: number; nombre: string; modo: string; pay_mode: string; horas: number | null; base: number | null;
  sin_tarifa: boolean; sin_lista: number; no_dadas: number; calculado: number; registrado: number; registros: number; diferencia: number;
}
export interface Presupuesto {
  mes: string; from: string; to: string; titulo: string; subtitulo: string;
  filas: PresupuestoFila[];
  // 25-oct-2026 (B2 ampliado): con objetivos capturados en Finanzas › Presupuesto › Ingresos, `total` es la suma de las filas con
  // presupuesto y `conObjetivo` = true; si no, mensualidades activos × plan como siempre.
  presupuesto: { total: number; alumnas: number; nota: string | null; conObjetivo?: boolean; filasConPresupuesto?: number; etiqueta?: string };
  real: { total: number; n: number };
  caja: { total: number; n: number; otrosMeses: number; adelantosDados: PresupuestoNT; nota: string | null };
  variacion: { abs: number; pct: number | null; nota: string };
  mensualidadesOrigen: { este_mes: PresupuestoNT; adelantos_recibidos: PresupuestoNT; atrasadas: PresupuestoNT; paquetes: PresupuestoNT };
  mesAnterior: { mes: string; titulo: string; real: number; diff: number; pct: number | null };
  siguiente: { mes: string; titulo: string; presupuesto: number; alumnas: number; yaCobrado: number; pagos: number; nota: string };
  nomina: { porProfe: PresupuestoProfe[]; calculada: number; registrada: number; sinTarifa: number; nota: string | null };
  gastos: { porCategoria: { categoria: string; n: number; total: number; nomina: boolean }[]; reales: number; realesSinNomina: number; nominaRegistrada: number; programados: number; recurrentes: { nombre: string; importe: number; categoria: string; dia: number }[]; nota: string | null };
  resumen: { ingresosPresupuesto: number; ingresosReal: number; ingresosCaja: number; nominaCalculada: number; nominaRegistrada: number; gastosReales: number; gastosProgramados: number; resultado: number; incompleto: boolean; notas: string[];
    pronostico?: boolean; ingresosPronostico?: number; nominaPronostico?: number; gastosPresupuestados?: number; etiqueta?: string; formula?: string };
  /** Mes FUTURO (Mabel 23-oct-2026): modo «Pronóstico» con su detalle. Meses en curso y pasados no lo traen. */
  modo?: 'pronostico';
  pronostico?: PresupuestoPronostico | null;
  /** «Mi escenario» (Mabel 23-oct-2026): overrides que ella editó encima del pronóstico. Solo meses futuros. */
  escenario?: PresupuestoEscenario | null;
  /** Apartado › Categoría › Concepto (catálogo de Mabel, 7-oct-2026). null si falló al armarla. */
  estructura?: PresupuestoEstructura | null;
  /** Indicadores de cabecera (Mabel 4-nov-2026, #1643): utilidad operativa real + presupuestado / generado / gastado /
   *  pendiente / flujo (lib/letsmoveKpis.js). null en meses futuros o si falló: el front enseña las tarjetas de antes. */
  kpis?: PresupuestoKpis | null;
  generado: string; archivo: string;
}
export interface PresupuestoKpis {
  formula: string;
  utilidadOperativa: { real: number; margenPct: number | null; conNominaCalculada: number | null; nominaSinRegistrar: number;
    sinNominaRegistrada: boolean; vsPresupuesto: number | null; enCurso: boolean };
  presupuestado: { ingresos: number; ingresosEtiqueta: string | null; gastos: number | null; utilidad: number | null; disponible: boolean };
  generado: { ingresos: number; cobros: number; variacion: number; variacionPct: number | null };
  gastado: { total: number; nomina: number; operacion: number; marketing: number; inversiones: number; vsPresupuesto: number | null };
  pendiente: { total: number; nomina: number; nominaVencida: number; gastos: number | null; porCobrar: { total: number; cargos: number } };
  flujo: { entro: number; cobros: number; adelantos: number; salio: number; pagos: number; neto: number };
}
export interface PresupuestoPronostico {
  mes: string; titulo: string; base: string; baseTitulo: string; diaCorte: number; etiqueta: string; nota: string;
  ingresos: { porConcepto: { k: string; categoria: string; concepto: string; monto: number; n: number; origen: string; festival?: number }[]; total: number; mensualidades: number; otros: number };
  nomina: { horario: number; comision: number; comisionDelMes: number; total: number; comisionEstimada: boolean };
  gastos: { total: number; porApartado: { section: string; apartado: string; total: number }[];
    detalle: { key?: string | null; apartado: string; section: string; categoria: string; concepto: string; monto: number; origen: string | null; desde: string | null }[];
    excluidos: { concepto: string; monto: number; motivo: string }[]; disponible: boolean; vacio: boolean };
  resultado: number; notas: string[]; formula: string;
}
export interface PresupuestoEstructura {
  secciones: { section: string; label: string; total: number;
    categorias: { category_id?: number; name: string; total: number; conceptos: { concept_id?: number; name: string; n: number; total: number }[] }[] }[];
  totales: { ingresos: number; egresos: number; nomina: number; inversiones: number };
  resultado: number; resultadoNominaCalculada: number; nominaCalculada: number | null; notas: string[];
}
// «Mi escenario» del Pronóstico (Mabel 23-oct-2026): campo → número. Solo se guardan los campos editados.
// Campos: ing:mensualidad:n (alumnos) · ing:<k> · nom:horario · nom:comision · gas:<key> · gas:extra.
export type EscenarioOv = Record<string, number>;
export interface PresupuestoEscenario { mes: string; ov: EscenarioOv; updated_at: string | null; updated_by: number | null }
export function guardarEscenario(month: string, ov: EscenarioOv) {
  return api.put<{ ok: true; mes: string; ov: EscenarioOv; updated_at: string | null }>('/finances/presupuesto/escenario', { month, ov });
}
export function borrarEscenario(month: string) {
  return api.del<{ ok: true }>(`/finances/presupuesto/escenario?month=${encodeURIComponent(month)}`);
}
export function getPresupuesto(month: string) { return api.get<Presupuesto>(`/finances/presupuesto?month=${month}`); }
export function urlExportPresupuesto(month: string, fmt: 'xlsx' | 'pdf') {
  return `/api/letsmove/finances/presupuesto.${fmt}?month=${encodeURIComponent(month)}`;
}

// ── Cobranza del mes ("¿a quién le toca cobrar?") ──
export type CollectionStatus = 'paid' | 'partial' | 'pending';
export interface CollectionStudent {
  account_id: number; student_name: string; has_photo: boolean;
  phone_prefix: string | null; phone_number: string | null;
  membership_name: string; discount_name: string | null;
  expected: number; paid: number; remaining: number;
  last_pay: string | null; status: CollectionStatus;
  /** Precio de lista; `expected` puede ser menor si se ACORDÓ otro importe (23-sep-2026). */
  list_price?: number; agreement?: string | null;
}
export interface CollectionResp {
  month: string;
  data: CollectionStudent[];
  summary: {
    billable: number; paid_count: number; partial_count: number; pending_count: number;
    expected_total: number; collected_total: number; pending_total: number;
    /** Becas del mes (7-oct-2026): cuántos y cuánto se deja de cobrar. Los de beca completa no salen en `data`. */
    scholarship_count?: number; scholarship_total?: number;
  };
  scholarships?: { account_id: number; student_name: string; pct: number; forgiven: number }[];
}
export interface RevisionFamilia {
  contact_id: number; nombre: string; descuento: string; estado: 'sin_familia' | 'familia_inactiva'; texto: string;
  familia: { contact_id: number; nombre: string; activa: boolean }[];
  sugerencia?: { contact_id: number; nombre: string; razones: string[] }[];
}
export function getRevisionFamilia(month: string) {
  return api.get<{ month: string; revisar: RevisionFamilia[] }>(`/finances/family-discounts/check?month=${month}`);
}
export function getCollection(month?: string) {
  return api.get<CollectionResp>(`/finances/collection${month ? `?month=${month}` : ''}`);
}

// ── MULTAS MES A MES (23-sep-2026) — GET /finances/late-fees/month ──
export type MotivoMulta =
  | 'multa_pendiente' | 'multa_cobrada' | 'multa_perdonada' | 'multa_transferencia_tarde' | 'por_multar'
  | 'a_tiempo' | 'prorroga' | 'tolerancia' | 'transferencia' | 'desfasado' | 'exenta' | 'no_vence'
  | 'saldado_sin_pago' | 'sin_clase' | 'nunca_vino' | 'baja' | 'cancelada' | 'fecha_incoherente'
  | 'mes_cerrado' | 'tarde_sin_multa';
export interface ExentaMulta { contact_id: number; month: string; nombre: string | null; motivo: string }
export interface AlumnaMulta {
  contact_id: number; nombre: string; plan: string | null; debt_id: number | null;
  due_date: string | null; limite_multa: string | null; monto: number; pagado: number; debe: number;
  fecha_pago: string | null; metodos: string[]; desfasado: boolean; exenta: ExentaMulta | null;
  fecha_wire?: string | null; limite_wire?: string | null;
  motivo: MotivoMulta; detalle: string | null;
  multa: { debt_id: number; amount: number; creada: string; source: string; cancel_reason: string | null } | null;
}
export interface MultasMes {
  month: string; hoy: string; es_mes_actual: boolean;
  reglas: { due_day: number; primera_clase: boolean; grace_days: number; fee: number;
    tolerance_days: number; skip_wire: boolean; skip_cycle: boolean; wire_window_days?: number };
  prorroga: string | null; exentas: ExentaMulta[];
  motivos: Record<MotivoMulta, string>; resumen: Record<MotivoMulta, number>; alumnas: AlumnaMulta[];
}
export function getMultasMes(month: string) { return api.get<MultasMes>(`/finances/late-fees/month?month=${month}`); }

// ── Catálogo de Finanzas (7-oct-2026, Mabel): apartado › categoría › concepto ───────────────────
// La regla vive en el backend (lib/letsmoveCatalogo.js). Ingresos: cada cobro cae solo en su concepto;
// en un cobro «Otro» se puede elegir (clase privada, renta…).
export type FinSection = 'ingreso' | 'egreso' | 'nomina' | 'inversion';
export interface FinConcept { concept_id: number; category_id: number; name: string; sort: number; active: boolean; system_key: string | null }
export interface FinCategory { category_id: number; section: FinSection; name: string; sort: number; active: boolean; system_key: string | null; conceptos: FinConcept[] }
export interface FinCatalog { section: FinSection; label: string; categorias: FinCategory[] }
export function getCatalog(all = false) {
  return api.get<{ data: FinCatalog[]; iva_default: number; gasto_fijo?: boolean }>(`/finances/catalog${all ? '?all=1' : ''}`);
}
export function createFinCategory(section: FinSection, name: string) {
  return api.post<{ ok: true; category_id: number }>('/finances/catalog/categories', { section, name });
}
export function patchFinCategory(id: number, patch: { name?: string; active?: boolean; sort?: number }) {
  return api.patch<{ ok: true }>(`/finances/catalog/categories/${id}`, patch);
}
export function createFinConcept(categoryId: number, name: string) {
  return api.post<{ ok: true; concept_id: number }>('/finances/catalog/concepts', { category_id: categoryId, name });
}
export function patchFinConcept(id: number, patch: { name?: string; active?: boolean; sort?: number; category_id?: number }) {
  return api.patch<{ ok: true }>(`/finances/catalog/concepts/${id}`, patch);
}
